import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { PhoneCall, PhoneOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useCallStore } from '@/stores/useCallStore'
import { estIdentiteAgent } from '@/lib/identiteAgent'
import { useCallActions } from '@/hooks/useCallActions'
import { useUIStore } from '@/stores/useUIStore'

/**
 * Modale globale d'appel — visible UNIQUEMENT quand un appel n'est pas encore connecté.
 *
 * 2 modes :
 *  1. Inbound non connecté → Accept / Reject
 *  2. Outbound en sonnerie → Hang up
 *
 * Une fois l'appel accepté/connecté, la modale disparaît et :
 * - Le dashboard bascule auto vers la conversation du contact (`callizy:open-conversation`)
 * - Les contrôles d'appel passent à l'InCallPanel inline (sur la timeline du contact)
 *   ou au widget flottant InCallWidget (si l'agent quitte la timeline)
 */
export function GlobalCallModal() {
  const { t } = useTranslation()
  const darkMode = useUIStore((s) => s.darkMode)
  const [mounted, setMounted] = useState(false)

  const showGlobalIncomingCall = useCallStore((s) => s.showGlobalIncomingCall)
  const isCallConnected = useCallStore((s) => s.isCallConnected)
  const callerPhoneNumber = useCallStore((s) => s.callerPhoneNumber)
  const callerContactName = useCallStore((s) => s.callerContactName)
  const callDirection = useCallStore((s) => s.callDirection)
  const isInternalCall = useCallStore((s) => s.isInternalCall)
  const { accept, reject } = useCallActions()

  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  // Une fois connecté, la modale disparaît (les contrôles passent dans la sidebar)
  if (!mounted || !showGlobalIncomingCall || isCallConnected) return null

  // Appel d'un collègue : jamais l'identité brute (client:agent_…) à l'écran.
  const interne = isInternalCall || estIdentiteAgent(callerPhoneNumber)
  const numeroAffiche = interne ? '' : callerPhoneNumber
  const initials = getInitials(callerContactName, numeroAffiche)

  // Mode 1 : Appel entrant non accepté
  if (callDirection === 'inbound') {
    return createPortal(
      <Wrapper darkMode={darkMode}>
        <div className="p-4">
          <div className="flex items-center gap-3 mb-4">
            <Avatar initials={initials} color="green" />
            <div className="flex-1 text-left min-w-0">
              <p
                className={cn(
                  'font-bold text-base truncate',
                  darkMode ? 'text-white' : 'text-slate-900',
                )}
              >
                {callerContactName || numeroAffiche || (interne ? t('phone.internalCall', 'Appel interne') : 'Numéro inconnu')}
              </p>
              <p
                className={cn(
                  'text-xs truncate',
                  darkMode ? 'text-slate-400' : 'text-slate-600',
                )}
              >
                {interne ? t('phone.internalCall', 'Appel interne') : callerContactName ? callerPhoneNumber : 'Appel entrant'}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void accept()}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium rounded-full h-11 flex items-center justify-center gap-2 transition-colors"
            >
              <PhoneCall className="h-4 w-4" />
              {t('phone.accept', 'Accepter')}
            </button>
            <button
              type="button"
              onClick={reject}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white font-medium rounded-full h-11 flex items-center justify-center gap-2 transition-colors"
            >
              <PhoneOff className="h-4 w-4" />
              {t('phone.reject', 'Refuser')}
            </button>
          </div>
        </div>
      </Wrapper>,
      document.body,
    )
  }

  // Mode 2 : Appel sortant en sonnerie
  return createPortal(
    <Wrapper darkMode={darkMode}>
      <div className="p-4">
        <div className="flex items-center gap-3 mb-4">
          <Avatar initials={initials} color="blue" />
          <div className="flex-1 text-left min-w-0">
            <p
              className={cn(
                'font-bold text-base truncate',
                darkMode ? 'text-white' : 'text-slate-900',
              )}
            >
              {callerContactName || numeroAffiche || (interne ? t('phone.internalCall', 'Appel interne') : 'Numéro inconnu')}
            </p>
            {interne ? (
              <span
                className={cn(
                  'text-xs font-medium px-2 py-0.5 rounded-full inline-block',
                  darkMode
                    ? 'bg-purple-900 text-purple-300'
                    : 'bg-purple-100 text-purple-700',
                )}
              >
                {t('phone.internalCall', 'Appel interne')}
              </span>
            ) : (
              <p
                className={cn(
                  'text-xs truncate',
                  darkMode ? 'text-slate-400' : 'text-slate-600',
                )}
              >
                {callerContactName ? callerPhoneNumber : 'Appel en cours…'}
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={reject}
          className="w-full bg-red-500 hover:bg-red-600 text-white font-medium rounded-full h-11 flex items-center justify-center gap-2 transition-colors"
        >
          <PhoneOff className="h-4 w-4" />
          {t('phone.hangup', 'Raccrocher')}
        </button>
      </div>
    </Wrapper>,
    document.body,
  )
}

interface WrapperProps {
  children: React.ReactNode
  darkMode: boolean
}

function Wrapper({ children, darkMode }: WrapperProps) {
  return (
    <div
      style={{
        zIndex: 999_999,
        position: 'fixed',
        top: 'max(16px, calc(env(safe-area-inset-top) + 8px))',
        right: '16px',
      }}
    >
      <div
        className={cn(
          'rounded-2xl shadow-2xl border w-[340px]',
          darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200',
        )}
      >
        {children}
      </div>
    </div>
  )
}

interface AvatarProps {
  initials: string
  color: 'green' | 'blue'
}

function Avatar({ initials, color }: AvatarProps) {
  return (
    <div
      className={cn(
        'flex items-center justify-center h-12 w-12 rounded-full flex-shrink-0',
        color === 'green' ? 'bg-green-500' : 'bg-blue-500',
      )}
    >
      <span className="text-white font-bold text-sm">{initials}</span>
    </div>
  )
}

function getInitials(name: string, phoneNumber: string): string {
  if (name) {
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0])
      .join('')
      .toUpperCase()
  }
  return phoneNumber ? phoneNumber.slice(-2) : '?'
}
